import React, {
    useRef, useImperativeHandle, MutableRefObject, useMemo,
    DependencyList,
} from "react";

export default function useOrCreateRef<T>(ref?: React.Ref<T>) {
    const internalRef = useRef<T | null>(null);
    useImperativeHandle(ref, () => internalRef.current as T);
    return internalRef;
}

export type DynamicParameter<T> = T extends (null | undefined) ? [] : [defaultValue: T];

class CombinedReference<T> implements MutableRefObject<T> {
    private references: React.Ref<T>[];

    private internalRef: T | undefined;

    constructor(refs: React.Ref<T>[], ...defaultValue: DynamicParameter<T>) {
        this.references = refs;
        this.internalRef = defaultValue?.firstElement;
    }

    get current() {
        return this.internalRef as T;
    }

    public set current(value: T) {
        this.internalRef = value;
        this.references.forEach(ref => {
            if (typeof ref === "function") {
                ref(value);
            } else if (ref) {
                // eslint-disable-next-line no-param-reassign
                (ref as {current: T}).current = value;
            }
        });
    }
}

export function useCombinedRef<T>(
    refs: React.Ref<T>[],
    ...defaultValue: DynamicParameter<T>
): MutableRefObject<T | null> {
    return useMemo(() => new CombinedReference<T>(refs, ...defaultValue), []);
}
